import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useNavigate,
} from 'react-router';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import { DevUserSwitcher } from '~/components/DevUserSwitcher';
import { resolveAuth } from '~/lib/session.server';

import type { Route } from './+types/root';
import './app.css';
import '~/sentry.client';
import { useI18n } from './hooks/useI18n';
import { Button } from './components/Button';

import 'dayjs/locale/nb';
import dayjs from 'dayjs';

dayjs.locale('nb');

export function headers(): HeadersInit {
  return {
    'Cache-Control': 'no-store',
    'X-Frame-Options': 'DENY',
    'X-Content-Type-Options': 'nosniff',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
    'Content-Security-Policy':
      "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https:; connect-src 'self' https://*.sentry.io",
  };
}

export async function loader({ request }: Route.LoaderArgs) {
  try {
    const ctx = await resolveAuth(request);
    return { sentryUser: { id: String(ctx.user.id), email: ctx.user.email } };
  } catch {
    return { sentryUser: null };
  }
}

export const links: Route.LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const page_language = {
  en: {
    go_back: 'Go back',
  },
  no: {
    go_back: 'Tilbake',
  },
};

export default function App({ loaderData }: Route.ComponentProps) {
  const [queryClient] = useState(() => new QueryClient());
  const sentryUser = loaderData.sentryUser;
  useEffect(() => {
    Sentry.setUser(sentryUser ?? null);
  }, [sentryUser]);
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <Toaster position="bottom-center" richColors />
      {import.meta.env.DEV && <DevUserSwitcher />}
    </QueryClientProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  const navigate = useNavigate();
  let title = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;
  const i18n = useI18n(page_language, 'no');

  if (error instanceof Error && !isRouteErrorResponse(error)) {
    Sentry.captureException(error);
  }

  if (error instanceof Error) {
    details = error.message;
  }

  if (isRouteErrorResponse(error)) {
    title = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    stack = error.stack;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-lg border border-red-200 bg-white p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-red-700">{title}</h1>
        <p className="text-gray-600">{details}</p>
        {stack && (
          <pre className="mt-4 w-full overflow-x-auto rounded bg-gray-100 p-4 text-left text-xs text-gray-700">
            <code>{stack}</code>
          </pre>
        )}
        <Button
          onClick={() => navigate(-1)}
          variant={'smallAccent'}
          className="mt-4"
        >
          ← {i18n.go_back}
        </Button>
      </div>
    </main>
  );
}
